import React from 'react'
import PropTypes from 'prop-types'
import { FormSection, reduxForm } from 'redux-form'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { Testable } from '@cred/react-testable'
import RadioGroup from 'components/radio-group'
import zipInput from 'components/input/zipInput'
import Input from 'components/input'
import MoneyInput from 'components/money-input'
import Checkbox from 'components/checkbox'
import Dropdown from 'components/dropdown'
import SubmitButton from 'components/submit-button'
import FilterFooter from 'multi-vertical/rate-table/components/filter-modal/footer'
import Font from '@cred/react-font'
import { validateZipWithPrefill, handleChangeZip } from 'common/validations/zip-code'
import { withStyles } from '@cred/react-theme'
import defaultStyles from './themes/default/styles.module.scss'
import homes1 from './themes/homes1/styles.module.scss'
import multiVerticalStyles from './multiVerticalStyles.module.scss'
import {
  propertyTypes,
  occupancyTypes,
  ficoTypes,
  verticalOptions,
  termsHeaderLabelShortVersion,
} from '../common/options'
import {
  PURCHASE_TYPE,
} from '../common/constants'

const FilterForm = props => {
  const {
    isPurchase,
    analyticsContexts,
    loadingProducts,
    handleSubmit,
    onSubmit,
    invalid,
    isMultiVertical,
    cancel,
    update,
    styles: defaultStylesProps,
    initialValues: { terms },
  } = props

  const styles = isMultiVertical ? multiVerticalStyles : defaultStylesProps
  const typeAndTermFontType = isMultiVertical ? 'bodySmall' : 'bodyLarge'

  return (
    <Testable className={isMultiVertical ? '' : 'mt-16'} testId="header-body">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className={styles.formBody}>
          <div className={styles.typeAndTerm}>
            <div className={`${styles.typeContainer} mb-24 mb-lg-0 mr-lg-24`}>
              <Font className={styles.title} type={typeAndTermFontType} weight="semiBold">Loan type:</Font>
              <Testable testId="loanType-buttons">
                <RadioGroup options={verticalOptions} name="loanType" />
              </Testable>
            </div>
            <div className={styles.termContainer}>
              <Font className={styles.title} type={typeAndTermFontType} weight="semiBold">Loan term:</Font>
              <Testable testId="loan-type-term">
                <FormSection className={styles.checkboxes} name="term">
                  {terms.map(term => (<Checkbox disableFullWidth name={term} label={termsHeaderLabelShortVersion[term]} className="mr-8" />))}
                </FormSection>
              </Testable>
            </div>
          </div>
          <div className={styles.fields}>
            <div className={styles.inputWrapper}>
              <Input
                className="mb-16"
                name={isPurchase ? 'zipCode' : 'zipCodeRefi'}
                label="Zip code"
                placeholder="Zip code"
                validate={validateZipWithPrefill}
                onChange={handleChangeZip}
                component={zipInput}
                analyticsContexts={analyticsContexts}
              />
            </div>
            <div className={styles.inputWrapper}>
              <MoneyInput
                className="mb-16"
                name={isPurchase ? 'estimatedValue' : 'homeValue'}
                label={isPurchase ? 'Purchase price' : 'Home value'}
                placeholder={isPurchase ? 'Purchase price' : 'Home value'}
              />
            </div>
            <div className={styles.inputWrapper}>
              <MoneyInput
                className="mb-16"
                name={isPurchase ? 'downPayment' : 'loanAmount'}
                label={isPurchase ? 'Estimated down payment' : 'Loan amount'}
                placeholder={isPurchase ? 'Estimated down payment' : 'Loan amount'}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Dropdown
                className="mb-16"
                label="Property type"
                name={isPurchase ? 'propertyType' : 'propertyTypeRefi'}
                options={propertyTypes}
                icon={false}
                analyticsOptions={{ fieldLabel: 'Property type' }}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Dropdown
                className="mb-16"
                label="Property usage"
                name={isPurchase ? 'occupancyType' : 'occupancyTypeRefi'}
                options={occupancyTypes}
                icon={false}
                analyticsOptions={{ fieldLabel: 'Property usage' }}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Dropdown
                className="mb-16"
                label="Credit score range"
                name={isPurchase ? 'ficoType' : 'ficoTypeRefi'}
                options={ficoTypes}
                icon={false}
                analyticsOptions={{ fieldLabel: 'Credit score range' }}
              />
            </div>
          </div>
        </section>
        {
          isMultiVertical ? (
            <FilterFooter
              cancel={cancel}
              update={update}
              invalid={invalid}
            />
          ) : (
            <SubmitButton
              data-test-id="submitBTN"
              className={styles.button}
              analyticsContexts={analyticsContexts}
              loading={loadingProducts}
              disabled={invalid}
            >
        Update
            </SubmitButton>
          )

        }
      </form>
    </Testable>
  )
}

FilterForm.propTypes = {
  /* eslint-disable-next-line */
  form: PropTypes.string.isRequired,
  isPurchase: PropTypes.bool.isRequired,
  invalid: PropTypes.bool,
  analyticsContexts: PropTypes.array,
  loadingProducts: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func,
  onSubmit: PropTypes.func,
  isMultiVertical: PropTypes.bool,
  styles: PropTypes.object,
  initialValues: PropTypes.object.isRequired,
  // Cancel and update are only
  // used for the multi-vertical widget
  cancel: PropTypes.func,
  update: PropTypes.func,
}

FilterForm.defaultProps = {
  analyticsContexts: [],
  styles: defaultStyles,
}

const mapStateToProps = ({ form }, { form: formName }) => {
  const formValues = form[formName] && form[formName].values
  return {
    isPurchase: typeof formValues === 'object' && formValues.loanType === PURCHASE_TYPE,
  }
}

export { FilterForm }

export default compose(
  withStyles(defaultStyles, { homes1 }),
  reduxForm({
    keepDirtyOnReinitialize: false,
    enableReinitialize: true,
    destroyOnUnmount: false,
  }),
  connect(mapStateToProps),
)(FilterForm)
